<template>
    <modal size="md" @close="reset(); $emit('close')" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title">Deactivate Deferred Compensation Plan</h3>
        </template>

        <slot>
            <div class="mb-2">
                <div class="p-1 unpad-x bg-150 rounded">
                    <div><span class="text-muted">Company: </span>{{ company.name }}</div>
                    <div><span class="text-muted">Deferred Compensation Plan: </span>{{ plan.display_name }}</div>
                </div>
            </div>

            <p>
                The plan keeps running through the date you choose. A replacement
                {{ plan.plan_type_pretty }} plan can then start the following day.
            </p>

            <div class="form-row">
                <div class="col-6">
                    <form-input
                        label="Last Day of Plan"
                        v-model="formData.ends_on"
                        :errors="errors.ends_on"
                        type="date"
                        :helper-text="`Must be ${earliestEndsOn} or later.`"
                    />
                </div>
            </div>

            <p v-if="replacementStartsOn">
                <small class="text-muted">A replacement plan can start on {{ replacementStartsOn }}.</small>
            </p>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row stacked>
                <button type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>

                <button type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Deactivate Plan
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import moment from 'moment'
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'

export default {
    props: ['client', 'plan', ],
    mixins: [FormMixin, ModalMixin],
    emits: ['updated', 'close', ],
    data() {
        return {
            formData: this.makeFormData(),
            formRules: {
                ends_on: {presence: {allowEmpty: false}, date: true},
            },
        }
    },
    computed: {
        company() {
            return this.client.companies.find(c => c.id == this.plan.company_id)
        },
        earliestEndsOnMoment() {
            const tomorrow = moment().add(1, 'days').startOf('day')
            const startsOn = moment(this.plan.starts_on).startOf('day')
            return startsOn.isAfter(tomorrow) ? startsOn : tomorrow
        },
        earliestEndsOn() {
            return this.earliestEndsOnMoment.format('MM/DD/Y')
        },
        // The date input emits MM/DD/YYYY, but the field still holds the API's ISO value
        // until it is edited, so both have to parse.
        endsOnMoment() {
            if (!this.formData.ends_on) {
                return null
            }

            return moment(this.formData.ends_on, ['MM/DD/YYYY', 'YYYY-MM-DD'], true)
        },
        replacementStartsOn() {
            const endsOn = this.endsOnMoment
            if (!endsOn || !endsOn.isValid()) {
                return ''
            }

            return endsOn.clone().add(1, 'days').format('MM/DD/Y')
        },
    },
    methods: {
        makeFormData() {
            return {
                ends_on: this.plan.ends_on ? this.plan.ends_on : '',
            }
        },
        validate() {
            this.errors = {}
            this.formErrors = []
            const originalValidate = FormMixin.methods.validate.bind(this)
            let isValid = originalValidate()

            const endsOn = this.endsOnMoment
            if (endsOn && endsOn.isValid() && endsOn.isBefore(this.earliestEndsOnMoment)) {
                isValid = false
                this.errors.ends_on = [`Must be ${this.earliestEndsOn} or later.`]
            }

            return isValid
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            this.$store.dispatch('START_LOADING')
            const url = `/clients/${this.client.id}/settings/deferred-comp-plans/${this.plan.id}/deactivate`
            this.$api.post(url, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`The plan will stop on ${resp.ends_on}.`)
                this.$emit('updated', resp)
                this.close()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                Object.assign(this.errors, errors)
                this.formErrors = (errors.__all__ || [])
                this.$bus.showError(errors.__all__)
            })
        },
    }
}
</script>
